import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowLeft, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { getCart, updateCartItem, removeFromCart } from "../api/cartApi";
import { toast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../components/ui/dialog";

const Cart = ({ onCartUpdate }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartTotal, setCartTotal] = useState(0);
  const [showBulkOrderDialog, setShowBulkOrderDialog] = useState(false);
  const navigate = useNavigate();

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await getCart();
      if (response.success && response.data.cart) {
        const cart = response.data.cart;
        setCartItems(cart.items || []);
        setCartTotal(cart.totalAmount || 0);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to load cart",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    if (newQuantity > 10) {
      setShowBulkOrderDialog(true);
      return;
    }

    try {
      const response = await updateCartItem(itemId, newQuantity);
      if (response.success && response.data.cart) {
        const cart = response.data.cart;
        setCartItems(cart.items || []);
        setCartTotal(cart.totalAmount || 0);
        if (onCartUpdate) onCartUpdate();
      }
    } catch (error) {
      console.error("Error updating cart:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to update cart",
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (itemId, productName) => {
    console.log("[Frontend Debug] handleRemove called with ID:", itemId);
    console.log("[Frontend Debug] Removing product:", productName);
    try {
      const response = await removeFromCart(itemId);

      if (response.success && response.data.cart) {
        const cart = response.data.cart;
        setCartItems(cart.items || []);
        setCartTotal(cart.totalAmount || 0);
        toast({
          title: "Removed from cart",
          description: `${productName} has been removed from your cart.`,
        });
        if (onCartUpdate) onCartUpdate();
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to remove item",
        variant: "destructive",
      });
    }
  };

  const handleCheckout = () => {
    navigate("/checkout");
  };
  // Helper: Calculate adjusted MRP including RAM/Storage/Warranty
  const calculateAdjustedMrp = (product, item) => {
    let mrp = Number(product?.mrp || product?.basePrice || 0);

    // RAM adjustment
    if (item.selectedConfig?.ram && product?.configurationVariants) {
      const ramVariant = product.configurationVariants.find(
        (v) => v.type === "RAM" && v.value === item.selectedConfig.ram,
      );
      if (ramVariant) {
        mrp += Number(ramVariant.priceAdjustment || 0);
      }
    }

    // Storage adjustment
    if (item.selectedConfig?.storage && product?.configurationVariants) {
      const storageVariant = product.configurationVariants.find(
        (v) => v.type === "STORAGE" && v.value === item.selectedConfig.storage,
      );
      if (storageVariant) {
        mrp += Number(storageVariant.priceAdjustment || 0);
      }
    }

    // Warranty adjustment
    if (item.selectedWarranty?.price) {
      mrp += Number(item.selectedWarranty.price || 0);
    }

    return mrp;
  };

  // Calculate savings (difference between MRP and actual price)
const savings = cartItems.reduce((sum, item) => {
  const product = item.productId;
  if (!product) return sum;

  const adjustedMrp = calculateAdjustedMrp(product, item);
  const unitPrice = Number(item.unitPrice || 0);

  const discountAmount = adjustedMrp - unitPrice;

  return sum + discountAmount * item.quantity;
}, 0);

 const subtotal = cartItems.reduce((sum, item) => {
  const product = item.productId;
  if (!product) return sum;

  const adjustedMrp = calculateAdjustedMrp(product, item);
  return sum + adjustedMrp * item.quantity;
}, 0);
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <p className="text-gray-600 dark:text-gray-300">Loading cart...</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <ShoppingBag className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600 mb-6" />
            <h2 className="text-3xl font-bold mb-4 dark:text-white">Your cart is empty</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Looks like you haven't added any products yet
            </p>
            <Link to="/all-products">
              <Button className="bg-black hover:bg-gray-800 text-white">
                Start Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/refurbished-laptops")}
          className="mb-6 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Continue Shopping
        </Button>

        <h1 className="text-4xl font-bold mb-8 dark:text-white">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              {cartItems.map((item, index) => {
                const product = item.productId;
                const productId = product?._id || product?.id || item.productId;
                const productName = product?.name || "Product";
                // Handle images array - check if it exists and has items
                let productImage = "/placeholder-image.jpg";
                if (
                  product?.images &&
                  Array.isArray(product.images) &&
                  product.images.length > 0
                ) {
                  productImage = product.images[0];
                } else if (product?.image) {
                  productImage = product.image;
                }
                // 🔥 Calculate Adjusted MRP properly
                let mrp = Number(product?.mrp || product?.basePrice || 0);

                // Add RAM adjustment
                if (
                  item.selectedConfig?.ram &&
                  product?.configurationVariants
                ) {
                  const ramVariant = product.configurationVariants.find(
                    (v) =>
                      v.type === "RAM" && v.value === item.selectedConfig.ram,
                  );
                  if (ramVariant) {
                    mrp += Number(ramVariant.priceAdjustment || 0);
                  }
                }

                // Add Storage adjustment
                if (
                  item.selectedConfig?.storage &&
                  product?.configurationVariants
                ) {
                  const storageVariant = product.configurationVariants.find(
                    (v) =>
                      v.type === "STORAGE" &&
                      v.value === item.selectedConfig.storage,
                  );
                  if (storageVariant) {
                    mrp += Number(storageVariant.priceAdjustment || 0);
                  }
                }

                // Add Warranty price
                if (item.selectedWarranty?.price) {
                  mrp += Number(item.selectedWarranty.price || 0);
                }

                const unitPrice = item.unitPrice || 0;
                const totalPrice = item.totalPrice || 0;
                const isB2B = item.quantity >= (product?.moq || 10);

                return (
                  <div key={item._id || productId}>
                    <div className="p-6">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <Link
                          to={`/product/${productId}`}
                          className="flex-shrink-0"
                        >
                          <img
                            src={productImage}
                            alt={productName}
                            className="w-24 h-24 object-cover rounded-lg"
                            onError={(e) => {
                              // Prevent infinite loop by checking if already set to placeholder
                              if (
                                e.target.src !==
                                window.location.origin +
                                  "/placeholder-image.jpg"
                              ) {
                                e.target.src = "/placeholder-image.jpg";
                              }
                            }}
                            loading="lazy"
                          />
                        </Link>

                        {/* Product Info */}
                        <div className="flex-1">
                          <Link to={`/product/${productId}`}>
                            <h3 className="font-semibold mb-2 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition">
                              {productName}
                            </h3>
                          </Link>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {product?.brand || ""}
                          </p>

                          {/* Selected Options */}
                          <div className="space-y-1 mb-3 text-sm">
                            {(item.selectedConfig?.ram || product?.specifications?.ram || item.selectedConfig?.storage || product?.specifications?.storage) ? (
                              <div className="flex flex-wrap gap-2 text-gray-600 dark:text-gray-300">
                                {(item.selectedConfig?.ram || product?.specifications?.ram) ? (
                                  <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                    RAM: {item.selectedConfig?.ram || product?.specifications?.ram}
                                  </span>
                                ) : null}
                                {(item.selectedConfig?.storage || product?.specifications?.storage) ? (
                                  <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                    Storage: {item.selectedConfig?.storage || product?.specifications?.storage}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {(item.selectedWarranty &&
                              item.selectedWarranty.duration &&
                              item.selectedWarranty.duration !== "Default") ? (
                                <div className="flex items-center text-blue-600 font-medium">
                                  <Shield className="w-3 h-3 mr-1" />
                                  <span>
                                    {item.selectedWarranty.duration} Warranty
                                  </span>
                                  {item.selectedWarranty.price > 0 ? (
                                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                      (+₹
                                      {item.selectedWarranty.price.toLocaleString()}
                                      )
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                          </div>
                          {isB2B ? (
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <Badge className="bg-orange-500 text-white text-xs">
                                  B2B Pricing
                                </Badge>
                                {product?.moq && (
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    MOQ: {product.moq}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-baseline space-x-2">
                                <span className="text-lg font-bold text-orange-600">
                                  ₹{unitPrice.toLocaleString()}
                                </span>
                                {mrp > unitPrice && (
                                  <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                    ₹{mrp.toLocaleString()}
                                  </span>
                                )}
                                <span className="text-sm text-orange-600 font-medium">
                                  per unit
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <Badge className="bg-blue-500 text-white text-xs">
                                  B2C Pricing
                                </Badge>
                              </div>
                              <div className="flex items-baseline space-x-2">
                                <span className="text-lg font-bold dark:text-white">
                                  ₹{unitPrice.toLocaleString()}
                                </span>
                                {mrp > unitPrice && (
                                  <>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                      ₹{mrp.toLocaleString()}
                                    </span>
                                    {product?.discountPercentage && (
                                      <span className="text-sm text-green-600 font-medium">
                                        {product.discountPercentage}% off
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Total:{" "}
                            <span className="font-semibold dark:text-white">
                              ₹{totalPrice.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex flex-col items-end justify-between">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemove(item._id || productId, productName)
                            }
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <div className="flex items-center space-x-2 border dark:border-gray-700 rounded-lg">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleQuantityChange(
                                  item._id || productId,
                                  item.quantity - 1,
                                )
                              }
                              disabled={item.quantity <= 1}
                            >
                              -
                            </Button>
                            <span className="w-8 text-center font-semibold">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleQuantityChange(
                                  item._id || productId,
                                  item.quantity + 1,
                                )
                              }
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < cartItems.length - 1 && <Separator />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sticky top-20">
              <h2 className="text-xl font-bold mb-6 dark:text-white">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Subtotal (
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)}{" "}
                    items)
                  </span>
                  <span className="font-semibold dark:text-white">
                    ₹{subtotal.toLocaleString()}
                  </span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Discount</span>
                    <span className="font-semibold text-green-600">
                      -₹{savings.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Delivery Charges</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">FREE</span>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between text-lg font-bold mb-6 dark:text-white">
                <span>Total Amount</span>
                <span>₹{cartTotal.toLocaleString()}</span>
              </div>

              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-6">
                <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                  You will save ₹{savings.toLocaleString()} on this order
                </p>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg"
              >
                Proceed to Checkout
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Bulk Order Dialog */}
      <Dialog open={showBulkOrderDialog} onOpenChange={setShowBulkOrderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Order Request</DialogTitle>
            <DialogDescription>
              For orders of 10 or more units, please contact our admin team for
              bulk pricing and special arrangements.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-lg font-semibold mb-2 dark:text-white">
              Please contact Admin for bulk order
            </p>
            <p className="text-center text-2xl font-bold text-blue-600 dark:text-blue-400">
              Phone: +91 9845044638
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            <Button
              onClick={() => {
                window.location.href = `tel:+919845044638`;
              }}
              className="bg-black hover:bg-gray-800 text-white"
            >
              Call Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cart;
